export interface PcvPreviewData {
  count: number;
  stride: number;
  positions: Float32Array;
}

function readMagic(view: DataView): string {
  return (
    String.fromCharCode(view.getUint8(0)) +
    String.fromCharCode(view.getUint8(1)) +
    String.fromCharCode(view.getUint8(2)) +
    String.fromCharCode(view.getUint8(3))
  );
}

function decodePcv1(buffer: ArrayBuffer, view: DataView): PcvPreviewData {
  const count = view.getUint32(4, true);
  const stride = view.getUint32(8, true);
  const expectedBytes = 12 + count * 3 * 4;
  if (buffer.byteLength < expectedBytes) {
    throw new Error(`PCV1 preview truncated (expected ${expectedBytes} bytes)`);
  }
  const positions = new Float32Array(buffer, 12, count * 3);
  return { count, stride, positions };
}

function decodePcv2(buffer: ArrayBuffer, view: DataView): PcvPreviewData {
  const count = view.getUint32(4, true);
  const stride = view.getUint32(8, true);
  const min = [
    view.getFloat32(12, true),
    view.getFloat32(16, true),
    view.getFloat32(20, true),
  ];
  const max = [
    view.getFloat32(24, true),
    view.getFloat32(28, true),
    view.getFloat32(32, true),
  ];
  const dataOffset = 36;
  const expectedBytes = dataOffset + count * 6;
  if (buffer.byteLength < expectedBytes) {
    throw new Error(`PCV2 preview truncated (expected ${expectedBytes} bytes)`);
  }

  const positions = new Float32Array(count * 3);
  const inv65535 = 1 / 65535;
  for (let i = 0; i < count; i++) {
    const base = dataOffset + i * 6;
    for (let axis = 0; axis < 3; axis++) {
      const q = view.getUint16(base + axis * 2, true);
      const lo = min[axis]!;
      const hi = max[axis]!;
      const span = hi - lo;
      positions[i * 3 + axis] = span > 1e-12 ? lo + q * inv65535 * span : lo;
    }
  }

  return { count, stride, positions };
}

function parsePcvBuffer(buffer: ArrayBuffer): PcvPreviewData {
  if (buffer.byteLength < 12) throw new Error('PCV preview file too small');

  const view = new DataView(buffer);
  const magic = readMagic(view);
  if (magic === 'PCV1') return decodePcv1(buffer, view);
  if (magic === 'PCV2') return decodePcv2(buffer, view);
  throw new Error(`Invalid PCV magic: ${magic}`);
}

async function gunzip(buffer: ArrayBuffer): Promise<ArrayBuffer> {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('Gzip PCV preview requires DecompressionStream (modern browser)');
  }
  const ds = new DecompressionStream('gzip');
  const stream = new Blob([buffer]).stream().pipeThrough(ds);
  return new Response(stream).arrayBuffer();
}

/** Load a PCV1/PCV2 point-preview blob (optionally gzip-compressed). */
export async function loadPcvPreview(url: string): Promise<PcvPreviewData> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`PCV preview fetch failed (${res.status})`);

  const raw = await res.arrayBuffer();
  const buffer = url.endsWith('.gz') ? await gunzip(raw) : raw;
  return parsePcvBuffer(buffer);
}
